import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import {
  clearStoredTokens,
  getStoredTokens,
  setStoredTokens,
} from '../auth/tokenStorage';
import type { AuthResponse } from './generated/boticaAPI.schemas';

// In dev, use a relative base so Vite's proxy handles routing (avoids CORS).
// In prod, VITE_API_URL is injected at build time by the Dockerfile.
const API_BASE_URL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || '');

export const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Orval mutator — every generated function calls this.
export const axiosInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  return instance.request<T>(config).then((res) => res.data);
};

// ── Interceptors ────────────────────────────────────────────────────────────

// Request: attach the stored access token.
instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const stored = getStoredTokens();
  if (stored?.accessToken) {
    config.headers.Authorization = `Bearer ${stored.accessToken}`;
  }
  return config;
});

// In-flight refresh guard — queued 401s share the single refresh result.
let refreshPromise: Promise<string> | null = null;

// Response: on 401, attempt one silent token refresh then retry.
instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

    // The refresh endpoint itself returned 401 — token expired/revoked/demoted.
    // Clear state and redirect; do NOT retry (that would cause an infinite loop).
    if (original?.url?.includes('/api/auth/admin/refresh')) {
      clearStoredTokens();
      window.location.assign('/');
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }

    const stored = getStoredTokens();
    if (!stored?.refreshToken) {
      clearStoredTokens();
      window.location.assign('/');
      return Promise.reject(error);
    }

    original._retry = true;

    if (!refreshPromise) {
      refreshPromise = instance
        .post<AuthResponse>('/api/auth/admin/refresh', { refreshToken: stored.refreshToken })
        .then((res) => {
          const { accessToken, refreshToken, expiresAt } = res.data;
          if (!accessToken || !refreshToken || !expiresAt) throw new Error('Incomplete refresh response');
          setStoredTokens({ accessToken, refreshToken, expiresAt });
          return accessToken;
        })
        .catch((err: unknown) => {
          clearStoredTokens();
          window.location.assign('/');
          throw err;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    const newToken = await refreshPromise;
    if (original.headers) {
      (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
    }
    return instance(original);
  },
);
